import { useEffect, useRef, useState } from 'react';
import {
  MAINTENANCE_PATH,
  MAINTENANCE_RETURN_KEY,
  isMaintenanceResponse,
  pingMaintenanceStatus,
} from 'api/maintenanceApi';

interface MaintenanceInfo {
  title?: string;
  message?: string;
  eta?: string | number | Date;
}

interface MaintenancePingResponse {
  status?: number;
  data?: unknown;
}

const PING_INTERVAL_MS = 30000;

function formatEta(eta: string | number | Date | undefined) {
  if (!eta) return '';
  const date = new Date(eta);
  if (Number.isNaN(date.getTime())) return String(eta);
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function Maintenance() {
  const [lastCheckedAt, setLastCheckedAt] = useState<Date | null>(null);
  const [maintenanceInfo, setMaintenanceInfo] = useState<MaintenanceInfo | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let cancelled = false;

    const resumeApp = () => {
      if (cancelled) return;
      const returnPath = localStorage.getItem(MAINTENANCE_RETURN_KEY) || '/dashboard';
      localStorage.removeItem(MAINTENANCE_RETURN_KEY);
      window.location.assign(returnPath === MAINTENANCE_PATH ? '/dashboard' : returnPath);
    };

    const readMaintenanceInfo = (res: MaintenancePingResponse) => {
      const data = res?.data;
      const payload = (data && typeof data === 'object' && 'data' in data ? data.data : data) ?? data;
      if (payload && typeof payload === 'object') {
        setMaintenanceInfo(payload as MaintenanceInfo);
      }
    };

    const pingBackend = async () => {
      try {
        const res = await pingMaintenanceStatus();
        setLastCheckedAt(new Date());
        if (isMaintenanceResponse(res)) {
          readMaintenanceInfo(res);
          return;
        }
        setMaintenanceInfo(null);
        resumeApp();
      } catch (err) {
        setLastCheckedAt(new Date());
        setMaintenanceInfo(null);
      }
    };

    pingBackend();
    intervalRef.current = setInterval(pingBackend, PING_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return (
    <div
      style={{
        minHeight: '70vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
      }}
    >
      <div
        style={{
          maxWidth: 640,
          width: '100%',
          background: 'rgba(255, 255, 255, 0.92)',
          borderRadius: 16,
          padding: '2.25rem 2.5rem',
          boxShadow: '0 20px 45px rgba(0, 0, 0, 0.12)',
          textAlign: 'center',
        }}
      >
        <h1 style={{ marginBottom: '0.75rem' }}>We are down for maintenance</h1>
        <p style={{ marginBottom: '1rem' }}>UrbanMetrix is temporarily unavailable while we roll out updates.</p>
        <p style={{ fontSize: '0.95rem', opacity: 0.8 }}>
          Checking every {PING_INTERVAL_MS / 1000} seconds
          {lastCheckedAt ? ` (last check: ${lastCheckedAt.toLocaleTimeString()})` : ''}
        </p>

        {maintenanceInfo && (
          <div
            style={{
              marginTop: '1.25rem',
              padding: '1rem 1.1rem',
              borderRadius: 12,
              background: 'rgba(15, 23, 42, 0.06)',
              textAlign: 'left',
              lineHeight: 1.5,
            }}
          >
            {maintenanceInfo.title && (
              <div style={{ fontWeight: 600, marginBottom: '0.35rem' }}>{maintenanceInfo.title}</div>
            )}
            {maintenanceInfo.message && <div style={{ marginBottom: '0.5rem' }}>{maintenanceInfo.message}</div>}
            {maintenanceInfo.eta && (
              <div style={{ fontSize: '0.95rem' }}>
                <strong>Expected end of maintenance:</strong> {formatEta(maintenanceInfo.eta)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
